<script setup lang="ts">
import { CheckBadgeIcon, PlusCircleIcon, TrashIcon } from '@heroicons/vue/24/outline'
import { storeToRefs } from 'pinia'
import { onMounted, ref } from 'vue'
import { RouterLink } from 'vue-router'

import type { DropdownOption } from '@/components/common/ui/inputs/inputTypes'
import type { BRMOrganizationBase } from '@/types/external/2work/doctypes/CustomDoctypes'
import type { DynamicLinkCreation } from '@/types/external/2work/doctypes/generatorOutput/Core/DynamicLinkCreation'

import { TEST_IDS_CREATE } from '@/_testIds/testIdsCreate'
import AvatarOrganization from '@/components/common/ui/avatar/AvatarOrganization.vue'
import ButtonTertiary from '@/components/common/ui/buttons/ButtonTertiary.vue'
import ThreedotActionsOnHover from '@/components/common/ui/inputs/ThreedotActionsOnHover.vue'
import ContextMenuItem from '@/components/common/ui/overlays/ContextMenuItem.vue'
import UserTag from '@/components/common/ui/UserTag.vue'
import { getOrganizationDetailRoute } from '@/routers/routes/mainRoutes/routeRelationships/routeOrganizationDetail'
import { useBackendConstantsStore } from '@/stores/backendConstantsStore'
import { translationFunctionDefault } from '@/utilities/translations'

import PersonAddOrganizationsForm from '../subForms/PersonAddOrganizationsForm.vue'

type CustomOrganizationLinkCreation = DynamicLinkCreation & {
  image?: string | undefined
}

const props = defineProps<{
  onAdd(orgLink: CustomOrganizationLinkCreation): Promise<void>
  onFormCancelled?(): void
  onRemove?(idx: number): void
  onSetPrimary?(idx: number): void
  organizationOptions: DropdownOption<BRMOrganizationBase>[]
  organizations: {
    description?: string | undefined
    doctype: string
    image?: string | undefined
    isPrimary?: boolean
    key: number | string
    linkPath: string
    linkTitle: string
    roleLabel?: string | undefined
    tagLabel?: string
  }[]
}>()

const tfDefault = translationFunctionDefault()

const formOpen = ref(false)
const openAddOrgForm = (): void => {
  formOpen.value = true
}
const onCancel = (): void => {
  formOpen.value = false
  props.onFormCancelled?.()
}
const onSubmitForm = async (values: CustomOrganizationLinkCreation): Promise<void> => {
  formOpen.value = false
  await props.onAdd(values)
}

const backendConstantsStore = useBackendConstantsStore()
const { fetchRoleList } = backendConstantsStore
const { roleOptionList } = storeToRefs(backendConstantsStore)

onMounted(async () => {
  await fetchRoleList()
})
</script>

<template>
  <section
    class="w-full space-y-5 border-b border-dotted border-b-black-200 px-8 py-5 last-of-type:border-none"
    :data-testid="TEST_IDS_CREATE.sectionOrganizationList.root"
  >
    <h2 class="font-medium">{{ tfDefault('editPerson.organizationSectionTitle') }}</h2>
    <ul :data-testid="TEST_IDS_CREATE.sectionOrganizationList.list">
      <li
        v-for="(org, idx) in props.organizations"
        :key="org.key"
        class="border-b border-dotted border-black-400 last:border-none"
        :data-testid="TEST_IDS_CREATE.sectionOrganizationList.item"
      >
        <ThreedotActionsOnHover class="right-0 top-4" position="top right" visibility="hover">
          <div class="flex justify-between py-4">
            <div class="flex gap-4 text-sm">
              <AvatarOrganization :image="org.image" size="big" />
              <div class="flex flex-col">
                <RouterLink
                  class="text-black-400 hover:text-lochmara-600"
                  :data-testid="TEST_IDS_CREATE.sectionOrganizationList.name"
                  :to="{
                    name: getOrganizationDetailRoute(org.doctype).name,
                    params: { id: org.key },
                  }"
                >
                  <span class="border-b border-current">
                    {{ org.linkTitle }}
                    <template v-if="org.isPrimary">
                      {{ tfDefault('editRelationGroup.sectionTitlePrimarySuffix') }}
                    </template>
                  </span>
                </RouterLink>
                <span class="min-h-5" :data-testid="TEST_IDS_CREATE.sectionOrganizationList.role">{{
                  org.roleLabel
                }}</span>
                <span
                  class="min-h-5"
                  :data-testid="TEST_IDS_CREATE.sectionOrganizationList.description"
                >
                  {{ org.description }}
                </span>
              </div>
            </div>
            <div v-if="org.tagLabel" class="flex flex-1 justify-end self-end">
              <UserTag :label="org.tagLabel" />
            </div>
          </div>
          <template #actions>
            <ContextMenuItem
              v-if="!org.isPrimary && props.onSetPrimary"
              :icon="CheckBadgeIcon"
              text="Als Primär"
              @click="() => props.onSetPrimary && props.onSetPrimary(idx)"
            />
            <ContextMenuItem
              v-if="props.onRemove"
              key="context-menu-item-2"
              :icon="TrashIcon"
              text="Entfernen"
              @click="() => props.onRemove && props.onRemove(idx)"
            />
          </template>
        </ThreedotActionsOnHover>
      </li>
    </ul>
    <ButtonTertiary
      v-if="!formOpen"
      :data-testid="TEST_IDS_CREATE.sectionOrganizationList.addOrganizationButton"
      :label="tfDefault('editPerson.organizationButtonLabelAdd')"
      @click="openAddOrgForm"
    >
      <PlusCircleIcon class="mr-2 h-6 w-6" />
    </ButtonTertiary>
    <PersonAddOrganizationsForm
      :open="formOpen"
      :organization-options="props.organizationOptions"
      :role-options="roleOptionList"
      :submit-form="onSubmitForm"
      @on-cancel="onCancel"
    />
  </section>
</template>
